import { z } from 'zod';

import { defineService } from '@@api/lib/defineService';
import {
    MaintenanceNote,
    Quote,
    ReleaseNote,
    ReleaseNoteConfig,
    UnityMaintenanceNote,
    UnityReleaseNote,
} from '@@api/services/releasenotes/schemas';
import {
    deserializeMaintenancenote,
    deserializeReleasenote,
    serializeMaintenancenote,
    serializeReleasenote,
} from '@@api/services/releasenotes/transformers';
import { PaginationParams } from '@@api/utils/schemas/queryParams';
import { paginated } from '@@api/utils/schemas/utils';
import config from '@@config';

export const releasenotesService = defineService(
    (defineEndpoint) => ({
        maintenanceNotes: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/maintenancenotes',
                responses: {
                    200: z.array(UnityMaintenanceNote.transform(deserializeMaintenancenote)),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/maintenancenotes/:id',
                omitContentLanguageFromKey: true,
                pathParams: z.object({
                    id: z.number(),
                }),
                responses: {
                    200: UnityMaintenanceNote.transform(deserializeMaintenancenote),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/maintenancenotes/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                body: MaintenanceNote.transform(serializeMaintenancenote),
                responses: {
                    200: UnityMaintenanceNote.transform(deserializeMaintenancenote),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/maintenancenotes',
                body: MaintenanceNote.transform(serializeMaintenancenote),
                responses: {
                    201: UnityMaintenanceNote.transform(deserializeMaintenancenote),
                },
            }),
        },
        quotes: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/quotes',
                query: z.object({
                    date: z.string(),
                }),
                responses: {
                    200: z.array(Quote),
                },
            }),
        },
        releasenote: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/releasenotes',
                query: z.object({
                    ...PaginationParams.pick({ sort: true }).shape,
                }),
                responses: {
                    200: paginated(UnityReleaseNote.transform(deserializeReleasenote)),
                },
            }),
            get: defineEndpoint({
                method: 'GET',
                path: '/releasenotes/:id',
                omitContentLanguageFromKey: true,
                pathParams: z.object({
                    id: z.number(),
                }),
                responses: {
                    200: UnityReleaseNote.transform(deserializeReleasenote),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/releasenotes/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                body: ReleaseNote.transform(serializeReleasenote),
                responses: {
                    200: UnityReleaseNote.transform(deserializeReleasenote),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/releasenotes',
                body: ReleaseNote.transform(serializeReleasenote),
                responses: {
                    201: UnityReleaseNote.transform(deserializeReleasenote),
                },
            }),
        },
        config: {
            get: defineEndpoint({
                method: 'GET',
                path: '/config',
                responses: {
                    200: ReleaseNoteConfig,
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/config',
                body: ReleaseNoteConfig,
                responses: {
                    200: ReleaseNoteConfig,
                },
            }),
        },
    }),
    {
        baseUrl: config.apiEnvVars.releasenotesUrl,
    },
);
